import type { DeliveryDateCalendarProps } from '@engyalo/delivery-ui-components'
import { CheckoutRules } from '../interfaces'
import type { ICheckoutRuleValue } from '../graphQL/queries/getCheckoutRule/types'
import type { RootState } from '../redux/store'
import { calendarDayInZone } from './calendarToday'
import { isHidePricesEnabled } from './hidePrices'

/**
 * The calendar props this adapter owns, checked against the library's own prop
 * shape.
 *
 * The same guard the shipped panels use, and the payment picker's sibling. It
 * guards names and value types, not presence — every prop here is optional, so
 * `{}` satisfies it and the colocated assertions cover omission instead.
 *
 * `onDateSelect` is deliberately absent. It is an authored action, bound
 * through `cinnamonActions` to
 * `templateActions.deliveryDate.setDeliveryDate`.
 *
 * `locale`, `previousMonthLabel` and `nextMonthLabel` are absent too, and stay
 * config's. The first is already authorable as `{{config.options.language}}`,
 * and the nav labels are copy.
 */
type InjectedDeliveryDateProps = Pick<
  DeliveryDateCalendarProps,
  'availableDates' | 'selectedDate' | 'hidePrices' | 'referenceDate'
>

/**
 * The wire's options, as the bare day tokens the calendar indexes.
 *
 * ⚠️ **R-8: the token goes in and comes back out untouched.** The storefront
 * re-derives its option list and matches by strict string equality, so a token
 * parsed into a `Date` and re-serialised is rejected for the same day the
 * shopper tapped. Both `'2026-08-13'` and `'2026-08-13T00:00:00.000Z'` are
 * legal inputs and the calendar hands each back in the shape it arrived in.
 * Nothing here may normalise one.
 *
 * `amount` is dropped, as on the payment side; every entry carries `0` today.
 *
 * An entry with no usable `data` is dropped rather than mapped — the array
 * arrives from untyped JSON by way of a checkout rule, so a `null` in it is
 * reachable and `null.data` throws.
 *
 * ⚠️ **The calendar's own drop rules are narrower than this one and it does
 * not export them.** `parseDayToken` rejects anything it cannot place — prose,
 * two-digit years, `'2026-02-30'` — and `indexAvailableDates` then drops
 * duplicates after normalising. So a rule returning five malformed strings is
 * non-empty here and empty on screen, and
 * `checkout.ui.DELIVERY_DATES.emptyHidden` will read `true` over a calendar
 * with nothing to pick. Do not reimplement `parseDayToken` chasing it: it is
 * component-internal on purpose, and a second copy would have to stay in sync
 * across releases.
 */
export const toDeliveryDates = (
  values?: ICheckoutRuleValue[] | null,
): string[] | undefined => {
  if (!Array.isArray(values)) return undefined

  return values
    .filter((value): value is ICheckoutRuleValue => !!value?.data)
    .map((value) => value.data)
}

/**
 * The four `DeliveryDateCalendar` props cinnamon owns rather than the config
 * author.
 *
 * Injected in `useCinnamonProps` for any component whose `importRoute` is
 * `DeliveryDateCalendar`. None is authorable as a `{{variable}}`: two are
 * shapes the config language cannot build, one is a predicate it cannot
 * compute, and one reads a clock.
 *
 * Everything else stays in config — `deliveryLabel`, `helperText`, the month
 * window, `weekStartsOn`, the colour props, `fieldsToDisplay`, both slots, the
 * nav labels and `onDateSelect`.
 */
export const getDeliveryDatePickerProps = (
  state: RootState,
): InjectedDeliveryDateProps => ({
  availableDates: toDeliveryDates(
    state.checkoutSlice.available[CheckoutRules.DELIVERY_DATES]?.data,
  ),
  /**
   * The shopper's pick, forwarded as stored.
   *
   * A value outside `availableDates` renders as no selection, which the
   * library calls the honest rendering of a session carrying a day the
   * storefront no longer offers. Reachable: the delivery rule recomputes from
   * the clock, so yesterday's pick is withdrawn by the passage of time rather
   * than by anything the shopper did.
   */
  selectedDate:
    state.checkoutSlice.selections[CheckoutRules.DELIVERY_DATES]?.data,
  /**
   * Which month the calendar opens on, and nothing else.
   *
   * ⚠️ Not a timezone for the dates. The calendar has no `timeZone` prop by
   * design, and pointing a tenant zone at a delivery *day* is the COM-816
   * defect — see `calendarDayInZone`. This anchors the month window only.
   */
  referenceDate: calendarDayInZone(
    state.defaultSlice.sessionData?.configuration?.i18n?.timezone,
  ),
  /**
   * The calendar renders no money — a date grid holds no amount — so this is
   * for the tenant strings. Both custom slots can reach any field on the
   * source object, and `maskPricesForDisplay` is an allowlist of prop *names*
   * over *numbers*.
   */
  hidePrices: isHidePricesEnabled(state),
})

export default getDeliveryDatePickerProps
